import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { itemService } from "../services/svc_items";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest } from "../types/common/commonTypes";

export const useTSQItemsMin = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.item.listv2(null, "min"),
    queryFn: () => itemService.listmin(),
    enabled: enabled,
  });
};

/**
 * Custom hook for fetching items with pagination and filtering
 */
export const useTSQItemList = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.item.list(params),
    queryFn: () => itemService.list(params),
    placeholderData: keepPreviousData, // Smooth pagination transitions - shows previous data while loading new
    enabled: enabled,
  });
};

/**
 * Custom hook for fetching a single item by ID
 */
export const useTSQItem = (id: string) => {
  return useQuery({
    queryKey: queryKeys.item.detail(id),
    queryFn: () => itemService.getById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

/**
 * Custom hook for deleting an item
 */
export const useTSMDeactivateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: itemService.deactivate,
    onSuccess: () => {
      // Invalidate all item lists to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.item.lists() });
    },
    onError: (error) => {
      console.error("Failed to delete item:", error);
    },
  });
};

/**
 * Custom hook for restoring a deleted item
 */
export const useTSMReactivateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: itemService.reactivate,
    onSuccess: () => {
      // Invalidate all item lists to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.item.lists() });
    },
    onError: (error) => {
      console.error("Failed to restore item:", error);
    },
  });
};

/**
 * Custom hook for creating a new item
 */
export const useTSMCreateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: itemService.create,
    onSuccess: () => {
      // Invalidate all item lists to include the new item
      queryClient.invalidateQueries({ queryKey: queryKeys.item.lists() });
    },
    onError: (error) => {
      console.error("Failed to create item:", error);
    },
  });
};

/**
 * Custom hook for updating an existing item
 */
export const useTSMUpdateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      itemService.update(id, data),
    onSuccess: (updatedItem) => {
      // Update the specific item in cache
      queryClient.setQueryData(
        queryKeys.item.detail(updatedItem.id),
        updatedItem,
      );
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: queryKeys.item.lists() });
    },
    onError: (error) => {
      console.error("Failed to update item:", error);
    },
  });
};
